<template>
  <v-container>
    <v-card>
      <v-card-title> Личная информация </v-card-title>
      <v-card-text>
        <property-form
          :value="user"
          :fields="
            [
              'email',
              'first_name',
              'last_name',
              'referral_code',
              'phone',
              'inn',
              'ogrn',
            ].reduce((a, v) => {
              a[v] = { visible: true };
              return a;
            }, {})
          "
          @input="doUpdateUser"
        />
      </v-card-text>
    </v-card>
    <v-card class="my-5">
      <v-card-title> Глобальные настройки </v-card-title>
      <v-card-text>
        <property-form
          :value="propertiesValues"
          :fields="propertiesFields"
          @input="saveProperties"
          :simulate-reactivity="true"
        />
      </v-card-text>
    </v-card>
    <v-card class="my-5">
      <v-card-title class="d-flex justify-space-between align-center">
        <span>Новости</span>
        <v-btn
          color="#d86568"
          large
          rounded
          class="pa-5 px-sm-10 white--text"
          @click="addNews"
        >
          <v-icon left>mdi-plus</v-icon>
          Добавить новость
        </v-btn>
      </v-card-title>
      <v-card-text>
        <object-grid-advanced
          :value="news"
          :field-defs="[
            { text: 'Заголовок', value: 'title', width: '35%' },
            { text: 'Активна', value: 'activeDisplay', width: '15%' },
            { text: 'Дата создания', value: 'createdAtDisplay', width: '25%' },
            { text: 'Истекает', value: 'expiresAtDisplay', width: '25%' },
          ]"
          :show-actions="{
            edit: true,
            delete: true,
          }"
          @doEditItem="editNews"
          @doDeleteItem="deleteNews"
          :instanceId="`newsSearch`"
        />
      </v-card-text>
    </v-card>
    <object-grid-advanced
      class="mt-5"
      :value="categories"
      :field-defs="[
        { text: 'Категория', value: 'name', width: '30%' },
        {
          text: 'Стоимость курса',
          value: 'course_price',
          width: '20%',
        },
        {
          text: 'Количество сессий',
          value: 'session_count',
          width: '20%',
        },
        {
          text: 'Стоимость сессии',
          value: 'session_price',
          width: '20%',
        },
      ]"
      :show-actions="{
        edit: true,
      }"
      @doEditItem="editCategoryPrice"
      :instanceId="`preferenceSearch_active`"
    />
    <error-message-wrapper ref="error_wrapper" />
  </v-container>
</template>

<script>
import PropertyForm from "@/components/PropertyForm.vue";
import { mapActions, mapGetters } from "vuex";
import ObjectGridAdvanced from "@/components/ObjectGridAdvanced.vue";
import CategoryForm from "@/components/CategoryForm.vue";
import NewsForm from "@/components/NewsForm.vue";
import PromptForm from "@/components/PromptForm.vue";
import ErrorMessageWrapper from "@/components/ErrorMessageWrapper.vue";
import NSApi from "@/api/NSApi";
import { VTextarea } from "vuetify/lib/components";

export default {
  name: "AdminPreferenceView",
  components: { PropertyForm, ObjectGridAdvanced, ErrorMessageWrapper },
  data() {
    return {
      news: [],
    };
  },
  computed: {
    ...mapGetters(["user"]),
    ...mapGetters("categories", ["categories"]),
    ...mapGetters(["configProperty"]),

    propertiesValues() {
      return {
        patientSessionTimeout: this.configProperty("patientSessionTimeout"),
        aiRecommendationPrompt: this.configProperty("aiRecommendationPrompt"),
      };
    },

    propertiesFields() {
      return {
        patientSessionTimeout: {
          label: "Тайм-аут для пациентов",
          visible: this.user?.role === "admin",
          type: "number",
          rules: [
            (v) => /^[0-9]{1,5}$/.test(v) || "Целое число, не меньше нуля",
          ],
        },
        aiRecommendationPrompt: {
          label:
            "Предварительный промпт для AI помощника (индивидуальные рекомендации)",
          visible: this.user?.role === "admin",
          component: VTextarea,
          options: {
            rows: 8,
          },
          twoColumns: true,
        },
      };
    },
  },
  methods: {
    ...mapActions(["doRequestConfigProperty"]),
    ...mapActions(["doSetConfigProperty"]),
    ...mapActions(["doUpdateUser"]),
    ...mapActions("categories", ["request_categories", "update_category"]),
    ...mapActions("modals", ["showModalForm"]),

    async loadNews() {
      try {
        const newsData = await NSApi.instance.getNews({ scope: "internal" });
        this.news = newsData.map((item) => {
          const dateField = item.createdAt || item.created_at;
          let dateStr = "";
          if (dateField) {
            try {
              const date = new Date(dateField);
              if (!isNaN(date.getTime())) {
                dateStr = date.toLocaleDateString("ru-RU", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                });
              } else {
                dateStr = dateField;
              }
            } catch (e) {
              dateStr = dateField || "";
            }
          }
          let expiresAtStr = "";
          if (item.expiresAt) {
            try {
              const expiresDate = new Date(item.expiresAt);
              if (!isNaN(expiresDate.getTime())) {
                expiresAtStr = expiresDate.toLocaleDateString("ru-RU", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                });
              }
            } catch (e) {
              expiresAtStr = item.expiresAt;
            }
          }
          return {
            ...item,
            activeDisplay: item.active ? "Да" : "Нет",
            createdAtDisplay: dateStr,
            expiresAtDisplay: expiresAtStr || "—",
          };
        });
      } catch (error) {
        console.error("Error loading news:", error);
        if (this.$refs.error_wrapper) {
          this.$refs.error_wrapper.showError(error);
        }
      }
    },
    addNews() {
      this.showModalForm({
        component: NewsForm,
        width: 800,
        newsItem: {},
      }).then(async (payload) => {
        try {
          await NSApi.instance.createNews(payload);
          await this.loadNews();
        } catch (error) {
          if (this.$refs.error_wrapper) {
            this.$refs.error_wrapper.showError(error);
          }
        }
      });
    },
    editNews(newsItem) {
      const newsData = this.news.find((n) => n.id === newsItem.id);

      this.showModalForm({
        component: NewsForm,
        width: 800,
        newsItem: {
          id: newsData.id,
          title: newsData.title,
          content: newsData.content,
          image: newsData.image,
          active: newsData.active,
          expiresAt: newsData.expiresAt,
        },
      }).then(async (payload) => {
        try {
          await NSApi.instance.updateNews(newsItem.id, payload);
          await this.loadNews();
        } catch (error) {
          if (this.$refs.error_wrapper) {
            this.$refs.error_wrapper.showError(error);
          }
        }
      });
    },
    deleteNews(newsItem) {
      this.showModalForm({
        component: PromptForm,
        title: "Удаление новости",
        desc: `Вы уверены, что хотите удалить новость "${newsItem.title}"?`,
      }).then(async () => {
        try {
          await NSApi.instance.deleteNews(newsItem.id);
          await this.loadNews();
        } catch (error) {
          if (this.$refs.error_wrapper) {
            this.$refs.error_wrapper.showError(error);
          }
        }
      });
    },
    saveProperties(items) {
      for (let key in items)
        this.doSetConfigProperty({ name: key, value: items[key] });
    },
    editCategoryPrice(category) {
      this.showModalForm({
        component: CategoryForm,
        title: "Редактирование стоимости курса",
        desc: ``,
        session_price: category?.session_price || 0,
        course_price: category?.course_price || 0,
        session_count: category?.session_count || 0,
      }).then((payload) => {
        // session_count не отправляем: оно вычисляется на бэке по составу
        // каталога, колонки в БД больше нет (см. N7 в DB_REFACTORING.md).
        this.update_category({
          ...category,
          session_price: Number(payload.session_price),
          course_price: Number(payload.course_price),
        });
      });
    },
  },
  beforeMount() {
    this.request_categories();
    this.doRequestConfigProperty("patientSessionTimeout");
    this.doRequestConfigProperty("aiRecommendationPrompt");
    if (this.user?.role === "admin") {
      this.loadNews();
    }
  },
};
</script>

<style scoped></style>
